import React from "react";
import { Flex, Text, Button, Link } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { formatShortFormDate } from "#utils/datetime";
import { SPACING } from "#utils/spacing";
import { JIRA_BASE_URL } from "../../constants";
import { WORKFLOW_STATUS_LABELS } from "../constants";
import { WorkflowStatus } from "../types";
import * as styles from "./styles_vanilla.css";
import type { WorkflowHeaderProps } from "./types";

export const WorkflowHeader: React.FC<WorkflowHeaderProps> = ({ workflow, onClose, isGPX }) => {
  const isJira = workflow.source === "JIRA";
  const displayStatus =
    WORKFLOW_STATUS_LABELS[workflow.status as WorkflowStatus] ?? workflow.status;

  const breadcrumbItems = [
    workflow.workflowType,
    workflow.client?.name,
    workflow.entity?.name,
  ].filter(Boolean);

  return (
    <Flex flexDirection="column" gap={SPACING.MEDIUM}>
      {/* Title row + close button */}
      <Flex justifyContent="space-between" alignItems="flex-start">
        <Flex flexDirection="column" gap={SPACING.X_SMALL}>
          <Text variant="h2" data-testid="workflow-detail-title">
            {workflow.title}
          </Text>
          <Flex alignItems="center" gap={SPACING.X_SMALL}>
            {breadcrumbItems.map((item, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <Text variant="secondary-md" color={Colors.Neutral500}>
                    |
                  </Text>
                )}
                <Text variant="secondary-md" color={Colors.Neutral600}>
                  {item}
                </Text>
              </React.Fragment>
            ))}
            {workflow.externalRefId && (
              <>
                {breadcrumbItems.length > 0 && (
                  <Text variant="secondary-md" color={Colors.Neutral500}>
                    |
                  </Text>
                )}
                {isJira && !isGPX ? (
                  <Link
                    href={`${JIRA_BASE_URL}/browse/${workflow.externalRefId}`}
                    shouldOpenInNewTab
                    icon="external-link"
                    iconPosition="right"
                    iconVariant="outline"
                    iconSize="small"
                    data-testid="workflow-detail-external-ref"
                  >
                    {workflow.externalRefId}
                  </Link>
                ) : (
                  <Text
                    variant="secondary-md"
                    color={Colors.Neutral600}
                    data-testid="workflow-detail-external-ref"
                  >
                    {workflow.externalRefId}
                  </Text>
                )}
              </>
            )}
          </Flex>
        </Flex>
        <Flex gap={SPACING.X_SMALL} alignItems="center">
          <Button
            className={styles.iconButton}
            onClick={onClose}
            data-testid="workflow-detail-close-button"
            aria-label="Close"
            icon="times"
            variant="neutral"
          />
        </Flex>
      </Flex>

      {/* Metadata row: Status | Created date | Assigned to */}
      <Flex className={styles.metadataRow}>
        <Flex flexDirection="column" className={styles.metadataItem}>
          <Text variant="regular-md" data-testid="workflow-detail-status">
            {displayStatus}
          </Text>
          <Text variant="secondary-sm" color={Colors.Neutral600}>
            Status
          </Text>
        </Flex>

        <Flex flexDirection="column" className={styles.metadataItem}>
          <Text variant="regular-md" data-testid="workflow-detail-created-date">
            {formatShortFormDate(workflow.createdAt)}
          </Text>
          <Text variant="secondary-sm" color={Colors.Neutral600}>
            Created date
          </Text>
        </Flex>

        <Flex flexDirection="column" className={styles.metadataItem}>
          <Text variant="regular-md" data-testid="workflow-detail-assignee">
            {workflow.currentAssignee?.displayName || "Unassigned"}
          </Text>
          <Text variant="secondary-sm" color={Colors.Neutral600}>
            Assigned to
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default WorkflowHeader;
